import { ChangeDetectionStrategy, Component, effect, inject, input, InputSignal, model, ModelSignal, output, OutputEmitterRef, signal } from '@angular/core';
import { DialogModule } from 'primeng/dialog';
import { MultiSelectModule, MultiSelectRemoveEvent } from 'primeng/multiselect';
import { FileUploadComponent } from '../../file-upload/file-upload.component';
import { TranslatePipe } from '@ngx-translate/core';
import { FormsModule } from '@angular/forms';
import { TextareaModule } from 'primeng/textarea';
import { ButtonModule } from 'primeng/button';
import { Files, InvestigationResultDto, Tags } from '../../../interfaces';
import { ChipComponent } from "../../chip/chip.component";
import { API_URL } from '../../../utils';
import { DropdownModule } from 'primeng/dropdown';
import { SelectModule } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';

@Component({
  selector: 'lib-investigation-result-modal',
  imports: [DialogModule, MultiSelectModule, FileUploadComponent,
    TranslatePipe, FormsModule, TextareaModule, ButtonModule, ChipComponent, DropdownModule, SelectModule, InputTextModule],
  templateUrl: './investigation-result-modal.component.html',
  styleUrl: './investigation-result-modal.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class InvestigationResultModalComponent {
  apiUrl = inject(API_URL);
  header: InputSignal<string> = input.required();
  showModal: ModelSignal<boolean> = model.required();
  investigationStatuses: InputSignal<any> = input.required()
  showChips: InputSignal<boolean> = input.required()
  savedAmount = input<number | null>(null);
  savedAmountChange = output<number>();
  tempSavedAmount = signal<number | null>(null);
  tempComment = '';
  tempTags: any[] = [];
  commentMarkedForDeletion = signal(false);
  lastCommentBeforeDelete = '';


  selectedInvestigationResult: InputSignal<InvestigationResultDto> = input.required()

  onFileRemove: OutputEmitterRef<Files> = output();
  onTagRemove: OutputEmitterRef<Tags> = output();
  onSubmit: OutputEmitterRef<any> = output();
  onFileUpload: OutputEmitterRef<File[]> = output();
  onDeleteComment: OutputEmitterRef<string> = output();

  selectedTags: any;
  isSubmitDisabled: boolean = false;
  selectedSingleTag: number | null = null;

  selectedResult = signal<InvestigationResultDto>({
    comment: '',
    investigationResultId: '',
    investigator: '',
    visibilityStatus: '',
    tags: [],
    files: []
  });

  constructor() {
    let wasVisible = false;

    effect(() => {
      this.tempSavedAmount.set(this.savedAmount());

      const visible = this.showModal();

      if (visible && !wasVisible) {
        const input = this.selectedInvestigationResult();
        this.tempComment = input.comment ?? '';
        this.tempTags = [...input.tags];
        this.commentMarkedForDeletion.set(false);
        this.lastCommentBeforeDelete = this.tempComment;

        this.selectedResult.set({
          ...input,
          comment: this.tempComment,
          tags: [...this.tempTags],
          files: [...input.files]
        });
      }

      wasVisible = visible;
    });
  }


  onRemoveFile(file: any) {
    this.selectedResult.update(current => ({
      ...current,
      files: []
    }));

    this.onFileRemove.emit(file);
  }

  onInvestigationResultSubmit() {
    this.isSubmitDisabled = true;
    this.onSubmit.emit({
      result: this.selectedResult(),
      commentDeleted: this.commentMarkedForDeletion(),
    });
    this.savedAmountChange.emit(this.tempSavedAmount() || 0);
  }

  onCommentChange(value: string): void {
    if (this.commentMarkedForDeletion()) {
      this.commentMarkedForDeletion.set(false);
    }
    this.selectedResult.update(current => ({
      ...current,
      comment: value
    }));
  }

  onDeleteCommentClick(): void {
    if (this.commentMarkedForDeletion()) {
      this.commentMarkedForDeletion.set(false);
      this.selectedResult.update(current => ({
        ...current,
        comment: this.lastCommentBeforeDelete
      }));
      return;
    }

    this.lastCommentBeforeDelete = this.selectedResult().comment ?? '';
    this.commentMarkedForDeletion.set(true);
    this.selectedResult.update(current => ({
      ...current,
      comment: ''
    }));
  }


  onChangeSingleTag(event: any) {
    const selectedName = event.value;
    const selectedTag = this.investigationStatuses().find(
      (t: any) => t.name === selectedName
    );

    if (!selectedTag) return;

    this.selectedResult.update(r => ({
      ...r,
      tags: [selectedTag]
    }));
  }


  uploadFiles(newFiles: File[]): void {
    if (!newFiles.length) return;

    const file = newFiles[0];
    this.onFileUpload.emit([file]);
    this.selectedResult.update(current => ({
      ...current,
      files: [
        {
          fileName: file.name,
          blobUri: '',
          isError: false,
        }
      ]
    }));
  }

  removeTag(event: MultiSelectRemoveEvent | Tags): void {
    let removedTag: Tags;

    if ((event as MultiSelectRemoveEvent).removed) {
      removedTag = (event as MultiSelectRemoveEvent).removed as unknown as Tags;
    }
    else {
      removedTag = event as Tags;
    }
    this.selectedResult.update(r => ({
      ...r,
      tags: r.tags.filter(t => t.id !== removedTag.id)
    }));
  }

}
